import { Link } from 'react-router-dom';
import {
  evaluateGoal,
  goalProgressPercent,
  goalStatusLabel,
  type GoalStatus,
} from './goalMetrics';
import styles from './SummaryMetricCard.module.css';

type Props = {
  label: string;
  value: string;
  goalTarget?: number | null;
  goalDirection?: string;
  actualForGoal?: number | null;
  goalLink?: string;
  formatTarget?: (n: number) => string;
  /** Dashboard hero: geen “Doel instellen” op elke tegel */
  hideGoalPrompt?: boolean;
  /** Tooltip / aria (dashboard glossary) */
  hint?: string;
};

function cardTone(status: GoalStatus): string {
  if (status === 'ok') return styles.cardOk;
  if (status === 'warn') return styles.cardWarn;
  if (status === 'bad') return styles.cardBad;
  return '';
}

export function SummaryMetricCard({
  label,
  value,
  goalTarget,
  goalDirection = 'min',
  actualForGoal,
  goalLink = '#doelen',
  formatTarget = (n) => String(n),
  hideGoalPrompt = false,
  hint,
}: Props) {
  const hasGoal = goalTarget != null && Number.isFinite(goalTarget);
  const status = hasGoal
    ? evaluateGoal(actualForGoal ?? null, goalTarget!, goalDirection)
    : 'none';
  const progress = hasGoal
    ? goalProgressPercent(actualForGoal ?? null, goalTarget!, goalDirection)
    : null;

  return (
    <article
      className={`${styles.card} ${cardTone(status)}`}
      title={hint}
      aria-label={hint ? `${label}: ${value}. ${hint}` : undefined}
    >
      <div className={styles.value}>{value}</div>
      <div className={styles.label}>
        {label}
        {hint ? (
          <abbr className={styles.labelHint} title={hint}>
            i
          </abbr>
        ) : null}
      </div>
      {hasGoal ? (
        <>
          <div
            className={`${styles.chip} ${styles[`chip_${status}` as keyof typeof styles] ?? styles.chip_unknown}`}
          >
            {goalStatusLabel(status)}
          </div>
          <div className={styles.goalLine}>
            Doel {formatTarget(goalTarget!)}
          </div>
          {progress != null ? (
            <div className={styles.progressTrack} aria-hidden>
              <div className={styles.progressFill} style={{ width: `${progress}%` }} />
            </div>
          ) : null}
        </>
      ) : hideGoalPrompt ? null : (
        <Link className={styles.setGoal} to={goalLink}>
          Doel instellen
        </Link>
      )}
    </article>
  );
}
